'use client';

import { useMutation } from '@apollo/client/react';
import { PortalModal, usePortalContext } from '@cloai/uikit';
import { isApolloError, getErrorCauseMessage } from '@/src/utils/error-handler';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { FC, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { twMerge } from 'tailwind-merge';

import { PORTALS } from '@/.constants/portals';
import { GENERATE_PHOTO_BY_PHOTO } from '@/.graphql';
import { GenerateButton } from '@/src/components/GenerateButton';
import { Header } from '@/src/components/Header';
import { useVitrinaPosition } from '@/src/hooks/useVitrinaPosition';
import {
  useStyleContext,
  useGTMContext,
  useProfileContext,
  useMobileContext,
} from '@/src/providers';
import { getSource } from '@/src/utils/getSource';

import { CloseAppButton } from '../_generate_image/CloseAppButton';
import { UploadButton } from '../_generate_image/UploadButton';

const styles = {
  portal: '!h-dvh !max-h-dvh !rounded-none !bg-none !bg-fill-menu !p-0',
  head: 'absolute top-0 left-0 right-0 z-20 py-2 px-5 flex justify-center',
  wrapper: 'shrink-0 bg-fill-menu',
  imageWrap:
    'relative shrink-0 flex flex-col items-center pb-9 mb-5 justify-end w-full aspect-square',
  image:
    'absolute w-full h-full top-0 left-0 object-cover object-top pointer-events-none',
  gradient: 'absolute top-0 left-0 right-0 bottom-0 bg-gradient-xl',
  name: 'relative text-2xl font-bold mb-3',
  innerWrap: 'relative p-5',
  info: 'flex flex-col gap-4 p-4 mb-4 bg-fill-quinary rounded-xl',
  infoTitle: 'text-sm font-bold mb-3',
  infoList: 'list-disc pl-4',
  infoListItem: 'text-sm font-medium text-label-secondary',
  carouselContainer: 'relative overflow-hidden w-full aspect-square',
  carousel: 'flex duration-700 cursor-pointer',
};

type UploadStep = 0 | 1;

export const GeneratePhotoByPhotoPortal: FC = () => {
  const t = useTranslations('imageModal');
  const { genderMode, prevPortal } = useStyleContext();

  const vitrinaPosition = useVitrinaPosition('photoByPhoto');

  const sendGTM = useGTMContext();
  const profile = useProfileContext()!;
  const { closePortal, openPortal } = usePortalContext();
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [photoByPhoto, { loading }] = useMutation(GENERATE_PHOTO_BY_PHOTO, {
    context: {
      headers: {
        'apollo-require-preflight': true,
      },
    },
  });
  const { isMobiles } = useMobileContext();

  const [referenceImage, setReferenceImage] = useState<File | null>(null);
  const [userImage, setUserImage] = useState<File | null>(null);

  const [uploadStep, setUploadStep] = useState<UploadStep>(0);

  const toastSuccessPhotoAddedIdRef = useRef<string | number | null>(null);

  const handleUploadButtonClick = () => {
    sendGTM({
      event: 'upload_photo',
      type: null,
      block: 'Фото по фото',
      category_name: null,
      name: null,
      category_position: null,
      vitrina_position: prevPortal === 'main' ? vitrinaPosition : null,
      gender: genderMode,
      source: getSource(prevPortal),
    });
  };

  const isRef = uploadStep === 0;

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    event.target.value = '';

    if (isRef) {
      setReferenceImage(file);
    } else {
      setUserImage(file);
    }

    sendGTM({
      event: 'upload_photo_complete',
      type: null,
      block: 'Фото по фото',
      category_name: null,
      name: null,
      category_position: null,
      vitrina_position: null,
      gender: genderMode,
      source: getSource(prevPortal),
    });

    if (toastSuccessPhotoAddedIdRef.current) {
      toast.dismiss(toastSuccessPhotoAddedIdRef.current);
      toastSuccessPhotoAddedIdRef.current = null;
    }

    toastSuccessPhotoAddedIdRef.current = toast(
      isRef ? t('successRefPhotoAdded') : t('successUserPhotoAdded'),
      {
        autoClose: 3000,
        position: 'bottom-center',
        icon: (
          <Image
            width={16}
            height={18}
            src="/icons/check.svg"
            alt="check"
            className="w-4 h-4"
          />
        ),
        theme: 'dark',
        style: {
          backgroundColor: '#3D3E41',
          color: '#FFFFFF',
          fontSize: '14px',
          fontWeight: '500',
          borderRadius: '12px',
          padding: '16px',
          bottom: '32px',
          maxWidth: 'calc(100% - 40px)',
          alignItems: 'start',
        },
      },
    );

    if (isRef) {
      setUploadStep(1);
    }
  };

  const handleClickGenerate = async () => {
    try {
      setButtonDisabled(true);

      sendGTM({
        event: 'Generate',
        type: null,
        block: 'Фото по фото',
        category_name: null,
        name: null,
        category_position: null,
        vitrina_position: null,
        source: getSource(prevPortal),
        gender: genderMode,
      });

      if (toastSuccessPhotoAddedIdRef.current) {
        toast.dismiss(toastSuccessPhotoAddedIdRef.current);
        toastSuccessPhotoAddedIdRef.current = null;
      }

      const { data } = await photoByPhoto({
        variables: {
          userImage,
          referenceImage,
          publicationId: null,
        },
      });

      if (!data) {
        throw new Error('No data');
      }

      const queueTime = data.photoByPhoto.queueTime;

      toast(
        <span>
          {t.rich('successMessage', {
            queueTime,
            b: (chunks: React.ReactNode) => <b>{chunks}</b>,
          })}
        </span>,
        {
          position: 'bottom-center',
          icon: (
            <Image
              width={16}
              height={18}
              src="/icons/check.svg"
              alt="check"
              className="w-4 h-4"
            />
          ),
          theme: 'dark',
          style: {
            backgroundColor: '#3D3E41',
            color: '#FFFFFF',
            fontSize: '14px',
            fontWeight: '500',
            borderRadius: '12px',
            padding: '16px',
            bottom: '32px',
            maxWidth: 'calc(100% - 40px)',
            alignItems: 'start',
          },
        },
      );

      sendGTM({
        event: 'Generation_complete',
        type: null,
        block: 'Фото по фото',
        category_name: null,
        name: null,
        category_position: null,
        vitrina_position: null,
        source: getSource(prevPortal),
        gender: genderMode,
      });

      setTimeout(() => {
        setButtonDisabled(false);
        setReferenceImage(null);
        setUserImage(null);
        setUploadStep(0);
      }, 5000);
    } catch (err) {
      const error = err as Error;
      if (isApolloError(error)) {
        const causeMessage = getErrorCauseMessage(error);
        if (causeMessage === 'not_enough_image_generations') {
          closePortal();
          openPortal(PORTALS.TopUpBalance);
        } else {
          toast.error(causeMessage || error.message);
        }
      } else {
        console.error(error);
      }

      sendGTM({
        event: 'Generation_error',
        type: null,
        block: 'Фото по фото',
        category_name: null,
        name: null,
        category_position: null,
        vitrina_position: null,
        source: getSource(prevPortal),
        gender: genderMode,
      });

      setTimeout(() => {
        setButtonDisabled(false);
        setReferenceImage(null);
        setUserImage(null);
        setUploadStep(0);
      }, 5000);
    }
  };

  const handleClosePortal = () => {
    if (prevPortal === 'allTools') {
      closePortal();
      openPortal(PORTALS.AllTools);
    } else {
      closePortal();
    }
  };

  const disabled = buttonDisabled || loading;

  return (
    <PortalModal
      portalId={PORTALS.GeneratePhotoByPhoto}
      className={styles.portal}
      noClose
    >
      <div className="h-full relative">
        <div className={twMerge(styles.head, isMobiles && 'pt-[100px]')}>
          <Header isCloseButton onClickCloseButton={handleClosePortal} />
        </div>

        <div
          className={twMerge(
            'h-full overflow-y-auto',
            isMobiles && 'pt-[100px]',
          )}
        >
          <div className={styles.wrapper}>
            <div className={styles.carouselContainer}>
              <div className={styles.carousel}>
                <div className={styles.imageWrap}>
                  {/* <Image
                fill
                src="/images/photo-by-photo.webp"
                alt="photoByPhoto"
                className={styles.image}
                quality={95}
              /> */}
                  <video
                    autoPlay
                    loop
                    muted
                    className={styles.image}
                    playsInline
                    webkit-playsinline="true"
                  >
                    <source
                      src={
                        profile?.lang === 'ru'
                          ? '/images/photo_by_photo_ru.mp4'
                          : '/images/photo_by_photo_eng.mp4'
                      }
                    />
                  </video>
                  <div className={styles.gradient} />
                  {/* <div className={styles.name}>{m('photoByPhotoTitle')}</div> */}
                </div>
              </div>
            </div>

            <div className={styles.innerWrap}>
              <div className={styles.info}>
                <div>
                  <div className={styles.infoTitle}>{t('info.title')}</div>
                  <ul className={styles.infoList}>
                    <li className={styles.infoListItem}>
                      {t('info.photoByPhotoText')}
                    </li>
                  </ul>
                </div>
                <div>
                  <div className={styles.infoTitle}>
                    {t('info.howItWorks.title')}
                  </div>
                  <ul className={styles.infoList}>
                    <li className={styles.infoListItem}>
                      {t('info.howItWorks.photoByPhotoText')}
                    </li>
                  </ul>
                </div>
              </div>

              {profile?.isAcitveStartFunnel ? (
                <CloseAppButton
                  text={t('buttonPay')}
                  keybord="paymentDownSale4"
                />
              ) : !Boolean(referenceImage && userImage) ? (
                <UploadButton
                  disabled={disabled}
                  isMultiple={false}
                  buttonText={
                    isRef
                      ? t('buttonUploadRefPhoto')
                      : t('buttonUploadUserPhoto')
                  }
                  onChange={handleFileChange}
                  onClick={handleUploadButtonClick}
                />
              ) : (
                <GenerateButton
                  disabled={disabled}
                  onClick={handleClickGenerate}
                  text={t('buttonPhotoByPhoto')}
                  price="5"
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </PortalModal>
  );
};
